import React, { useEffect, useState, useCallback } from "react";
import {
  getSellerProfile,
  updateSellerProfile
} from "../../api/sellerProfile.api";

import { useAuth } from "../../context/AuthContext";

import { Row, Col, Tooltip } from "reactstrap";
import { toast } from "react-toastify";

import StatusBadge from "../../components/StatusBadge";

const SellerProfile = ({ onBack }) => {
  const { user, setUser } = useAuth();
  const [values, setValues] = useState({
    business_name: "",
    owner_name: "",
    city: "",
    state: "",
    gst_number: "",
    email: "",
    contact_number: "",
    business_description: "",
    verification_status: "",
    type: "",
    created_at: ""
  });

  const [isEditing, setIsEditing] = useState(false);

  const [tooltips, setTooltips] = useState({
    backBtn: false,
    editBtn: false,
    cancelBtn: false,
    saveBtn: false
  });

  const toggleTooltip = (tooltipName) => {
    setTooltips(prev => ({
      ...prev,
      [tooltipName]: !prev[tooltipName]
    }));
  };

  // --- LOGIC (UNCHANGED) ---
  // useEffect(() => {
  //   getSellerProfile()
  //     .then(res => setValues(res.data.data || {}))
  //     .catch(err => console.log(err));
  // }, []);

  useEffect(() => {
    if(user){
      setValues(user);
    }
  }, [user]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    const updated = {
      ...values,
      verification_status: "UNVERIFIED"   // IMPORTANT
    };

    await updateSellerProfile(updated);

    setValues(updated);
    setUser(updated); 
    setIsEditing(false);

    toast.success("Profile updated. Verification reset.");
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "clamp(20px, 4vw, 30px)",
    marginBottom: "24px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
  };

  const labelStyle = { color: "#6b7280", fontSize: "14px" };
  const valueStyle = { color: "#112347", fontWeight: "600", fontSize: "14px" };
  const sectionTitleStyle = { color: "#112347", fontWeight: "700", fontSize: "16px", marginBottom: "20px" };

  return (
    <div className="container-fluid p-0">

      {/* 1. BACK BUTTON */}
      <div className="mb-4">
        <button
          id="backBtn"
          onClick={onBack}
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 fw-bold"
          style={{ color: "#112347", fontSize: "15px" }}
        >
          <span style={{ fontSize: "20px" }}>←</span> Back to Dashboard
        </button>
        <Tooltip placement="right" isOpen={tooltips.backBtn} target="backBtn" toggle={() => toggleTooltip('backBtn')}>
          Return to seller dashboard
        </Tooltip>
      </div>

      {/* 2. PROFILE HEADER CARD */}
      <div style={cardStyle}>
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-4 gap-3">
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: "60px", height: "60px", backgroundColor: "#f3f4f6", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" }}>
              👤
            </div>
            <div>
              <h3 className="fw-bold m-0" style={{ color: "#112347", fontSize: "clamp(20px, 3vw, 28px)" }}>{values.business_name || "Business Name"}</h3>
              <div className="d-flex align-items-center gap-3 mt-1 flex-wrap">
                <span className="text-muted small">🏭 {values.type}</span>
                <StatusBadge status={values.verification_status} />
              </div>
            </div>
          </div>
          <div className="text-md-end text-start">
            <button
              id="editBtn"
              onClick={() => setIsEditing(true)}
              className="btn btn-outline-dark fw-bold px-4 py-2"
              style={{ borderRadius: "8px", fontSize: "13px" }}
            >
              ✏️ Edit Profile
            </button>
            <Tooltip placement="bottom" isOpen={tooltips.editBtn} target="editBtn" toggle={() => toggleTooltip('editBtn')}>
              Edit your business profile
            </Tooltip>

            <p className="text-muted small mt-2 mb-0">
              Updating your profile will require re-verification.
            </p>
          </div>
        </div>

        <p className="text-muted" style={{ fontSize: "14px", lineHeight: "1.6", maxWidth: "90%" }}>
          {values.business_description || "No description provided for this business."}
        </p>

        <div className="mt-4 d-flex align-items-center gap-2 text-muted small">
          <span>📅</span> Member since {values.created_at ? values.created_at.split("T")[0] : "---"}
        </div>
      </div>

      {/* 3. DETAILS ROW */}
      <Row className="g-3 g-md-4">
        {/* Business Details */}
        <Col xs="12" lg="6">
          <div style={cardStyle}>
            <h6 style={sectionTitleStyle}>Business Details</h6>
            <div className="d-flex flex-column gap-3 mt-4">
              <div className="d-flex justify-content-between border-bottom pb-2">
                <span style={labelStyle}>Business Name</span>
                <span style={valueStyle}>{values.business_name}</span>
              </div>
              <div className="d-flex justify-content-between border-bottom pb-2">
                <span style={labelStyle}>Owner Name</span>
                <span style={valueStyle}>{values.owner_name}</span>
              </div>
              <div className="d-flex justify-content-between border-bottom pb-2">
                <span style={labelStyle}>Type</span>
                <span style={valueStyle}>🏭 {values.type}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span style={labelStyle}>GST Number</span>
                <span style={valueStyle}>{values.gst_number} <span className="text-success">✅</span></span>
              </div>
            </div>
          </div>
        </Col>

        {/* Contact Information */}
        <Col xs="12" lg="6">
          <div style={cardStyle}>
            <h6 style={sectionTitleStyle}>Contact Information</h6>
            <hr className="opacity-25" />
            <div className="d-flex flex-column gap-4 mt-4">
              <div className="d-flex align-items-center gap-3 text-break">
                <span className="fs-5">✉️</span>
                <span style={valueStyle}>{values.email}</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className="fs-5">📞</span>
                <span style={valueStyle}>{values.contact_number}</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className="fs-5">📍</span>
                <span style={valueStyle}>{values.city}, {values.state}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* 4. DOCUMENTS SECTION */}
      <div style={cardStyle}>
        <h6 style={sectionTitleStyle}>Uploaded Documents</h6>

        <div className="d-flex flex-column gap-3 mt-4">
          {values.documents ? (
            <div
              className="p-3 border rounded d-flex flex-wrap justify-content-between align-items-center gap-2"
              style={{ backgroundColor: "#f9fafb" }}
            >
              <div className="d-flex align-items-center gap-3">
                <span className="fs-3">📄</span>
                <div>
                  <div style={valueStyle}>{values.documents}</div>
                  <div className="text-muted small">
                    Uploaded on {values.created_at ? values.created_at.split("T")[0] : "---"}
                  </div>
                </div>
              </div>

              <a
                href={`https://aaeternitas.com/uploads/documents/${values.documents}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-link text-dark fw-bold text-decoration-none d-flex align-items-center gap-2"
              >
                👁️ View
              </a>
            </div>
          ) : (
            <div className="text-muted small">No document uploaded</div>
          )}
        </div>

        
      </div>


      {isEditing && (

        <div 
        onClick={() => setIsEditing(false)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          padding: "15px" // safety padding for small screens
        }}>
          <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            padding: "clamp(20px, 4vw, 30px)",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "650px",
            maxHeight: "90vh",
            overflowY: "auto",
            position: "relative"

          }}>

            <span
              onClick={() => setIsEditing(false)}
              style={{
                position: "absolute",
                top: "15px",
                right: "20px",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold"
              }}
            >
              ✕
            </span>

            <h5 className="fw-bold mb-4" style={{ color: "#112347" }}>
              Edit Profile
            </h5>

            {/* BUSINESS NAME */}
            <label className="fw-bold small mb-1">Business Name</label>
            <input
              className="form-control mb-3"
              value={values.business_name}
              onChange={e => setValues({...values, business_name: e.target.value})}
            />

            {/* OWNER NAME */}
            <label className="fw-bold small mb-1">Owner Name</label>
            <input
              className="form-control mb-3"
              value={values.owner_name}
              onChange={e => setValues({...values, owner_name: e.target.value})}
            />

            {/* CITY */}
            <label className="fw-bold small mb-1">City</label>
            <input
              className="form-control mb-3"
              value={values.city}
              onChange={e => setValues({...values, city: e.target.value})}
            />

            {/* STATE */}
            <label className="fw-bold small mb-1">State</label>
            <input
              className="form-control mb-3"
              value={values.state}
              onChange={e => setValues({...values, state: e.target.value})}
            />

            {/* GST */}
            <label className="fw-bold small mb-1">GST Number</label>
            <input
              className="form-control mb-3"
              value={values.gst_number}
              onChange={e => setValues({...values, gst_number: e.target.value})}
            />

            {/* EMAIL */}
            <label className="fw-bold small mb-1">Email</label>
            <input
              className="form-control mb-3"
              value={values.email}
              onChange={e => setValues({...values, email: e.target.value})}
            />

            {/* CONTACT */}
            <label className="fw-bold small mb-1">Contact Number</label>
            <input
              className="form-control mb-3"
              value={values.contact_number}
              onChange={e => setValues({...values, contact_number: e.target.value})}
            />

            {/* DESCRIPTION */}
            <label className="fw-bold small mb-1">Business Description</label>
            <textarea
              className="form-control mb-3"
              style={{ minHeight: "100px" }}
              value={values.business_description}
              onChange={e => setValues({...values, business_description: e.target.value})}
            />

            {/* BUTTONS */}
            <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
              <button
                id="cancelBtn"
                className="btn btn-light px-4 w-100 w-sm-auto"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
              <Tooltip placement="top" isOpen={tooltips.cancelBtn} target="cancelBtn" toggle={() => toggleTooltip('cancelBtn')}>
                Discard changes
              </Tooltip>

              <button
                id="saveBtn"
                className="btn px-4 text-white w-100 w-sm-auto"
                style={{ backgroundColor: "#112347" }}
                onClick={handleUpdate}
              >
                Save Changes
              </button>
              <Tooltip placement="top" isOpen={tooltips.saveBtn} target="saveBtn" toggle={() => toggleTooltip('saveBtn')}>
                Save your profile changes
              </Tooltip>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default SellerProfile;